// Visual height model: turns the physical grid into per-hex heights, biome weights and noise
// amplitudes for the mesher. Pure (no GL), runs in the mesh worker and on the main thread (picking).
//
// Heights are in world units (hex radius R = 1), quantised for Civ readability rather than true
// scale, with a small real-elevation term so plateaus (Tibet, Andes, Ethiopia) still read:
//
//      +0.8 ┤                 ▲ mountain crown
//           │              ▄▄███▄▄   hills: soft mound      Tibet ▄▄▄▄▄▄▄▄ (plateau term)
//      +0.1 ┤▁▁▁▁ flat ▁▁▁▄▟██████▙▄▁▁▁▁▄▄▆▆▄▄▁▁▁▁▁▁▁▁▁▁▁▁▁▁▟
//       0.0 ┤~~~~~~~~ sea level (water plane) ~~~~~~~~~~~~~~~
//      -0.1 ┤  coast floor (shelf, from bathymetry)
//      -0.6 ┤        ocean floor (deep hexes are not meshed)

import { HEX_COUNT, WORLD_W, neighbour } from '../../core/grid.ts';
import { FEATURE, HILL, TERRAIN } from '../../core/schema.ts';
import type { PhysicalGrid } from '../hexdata.ts';

/** Biome channels carried per vertex (two vec4 attributes). */
export const BIOME = { GRASS: 0, PLAINS: 1, DESERT: 2, TUNDRA: 3, SNOW: 4, FOREST: 5, JUNGLE: 6, SEABED: 7 } as const;
export const BIOME_COUNT = 8;

/** Per-hex aux channels: rock (mountain faces), marsh, floodplain. */
export const AUX = { ROCK: 0, MARSH: 1, FLOOD: 2 } as const;
export const AUX_COUNT = 3;

/**
 * Per-hex ground cover for the terrain shader (two RGBA8 texels per hex, hexdata.ts): the material
 * blends these between hex centres with a noise warp, so biome edges never follow hex outlines.
 * Boreal woods get their own dark conifer channel instead of a tint over tundra.
 */
export const GROUND = { GRASS: 0, PLAINS: 1, DESERT: 2, TUNDRA: 3, SNOW: 4, BROADLEAF: 5, CONIFER: 6, JUNGLE: 7 } as const;
export const GROUND_COUNT = 8;

/** Tree species on a woods hex: which ground channel (and which tree models) it gets. */
export const WOODS_KIND = { BROADLEAF: 0, CONIFER: 1, MIXED: 2 } as const;
export type WoodsKind = (typeof WOODS_KIND)[keyof typeof WOODS_KIND];

/** How a hex is meshed. */
export const HEX_KIND = { LAND: 0, WATER: 1, LAKE: 2, DEEP: 3 } as const;

/** Sea level: the ocean plane sits here. */
export const SEA_Y = 0;

const BYTE = 255;

// Land heights.
const LAND_BASE = 0.07;
const ELEV_GAIN = 0.12; // world units per km^ELEV_POW of mean elevation
const ELEV_POW = 0.8;
const METRES_PER_KM = 1000;
const HILL_LIFT = 0.12;
const HILL_CROWN = 0.28;
const MOUNTAIN_LIFT = 0.3;
const RELIEF_LIFT = 0.1; // extra mountain lift per km of relief
const MOUNTAIN_CROWN = 0.28;
const RELIEF_CROWN = 0.12; // extra mountain crown per km of relief
const MAX_RELIEF_KM = 2;
const LAKE_DIP = 0.035;
const HILL_ROCK = 0.05;
const CROWN_SCALE = [0.55, 1.3] as const; // per-hex random crown scale for mountains

// Sea floor heights from bathymetry.
const COAST_TOP = -0.07;
const COAST_DROP = 0.2;
const COAST_DEPTH_M = 200;
const OCEAN_TOP = -0.3;
const OCEAN_DROP = 0.35;
const OCEAN_DEPTH_M = 5000;

// Vertex noise amplitude (world units), plus a share of the hex's real relief (elevation standard
// deviation) so rough lowlands undulate and flat plains stay flat.
const AMP_FLAT = 0.014;
const AMP_HILLS = 0.045;
const AMP_MOUNTAINS = 0.13;
const AMP_PER_RELIEF_KM = 0.12;
const AMP_RELIEF_MAX_KM = 0.4;

// Snow comes from data only: the physical `ice` field (glacier / ice-sheet fraction of the hex).
// A mountain's cap covers the share of its peak model whose footprint area matches that fraction
// (radius (1 − f)^P: area above height fraction f is (1 − f)^(2P), so f = 1 − ice^(1/2P)). Other
// land with glacier ice gets snow patches covering that share. No seasonal snow: the map never
// claims weather.
const ICE_MIN = 0.03;
const ICE_CAP_MAX = 0.9;
/** Radius exponent P of the peak models (features/peakGeo.ts). */
export const PEAK_PROFILE = 1.2;
export const NO_SNOW = 60; // snow line (world y) of a hex that holds no glacier ice

// Peak models (features): height by the hex's highest point (elev_max), world units. An absolute
// scale, so a foothill hex with big local relief never matches Everest.
const PEAK_H = [0.35, 1.6] as const;
const PEAK_ELEV_M = [1500, 8000] as const;
const HERO_MIN_MEAN_M = 500; // a hex whose mean elevation is lower holds lumps, no horn
const HERO_RINGS = 2; // a horn is the highest point among the mountain hexes this close
const LUMP_HEIGHT = 0.55; // a lump-only hex's tallest model, as a share of its horn height
const HERO_TIE_SALT = 31;
const PEAK_BASE_CROWN = 0.3; // share of the crown under a peak model's base (it sits on the mound)

// A mountain-class hex looks mountainous only when it stands out of its surroundings: not on a
// lowland (a foothill strip on a plain, Terai), and not on a high plateau (Tibet) unless its
// highest point rises well above its own mean elevation (a massif). Otherwise it is rolling
// ground: hill-like relief and no peak model. Tunable presentation, derived from elev/elev_max.
const FOOT_M = [250, 800] as const; // mean elevation over which foothills turn into mountains
const PLATEAU_M = [3000, 4300] as const; // mean elevation over which a hex counts as plateau
const PROMINENCE_M = [900, 2600] as const; // highest point above the hex mean: steppe swell → massif
const ROLLING_BELOW = 0.25; // mountainness under this is rolling ground

// Feature ground mixes (share of the feature channel; the rest keeps the base terrain).
const WOODS_SHARE = 0.72;
const CONIFER_SHARE = 0.88;
const RAINFOREST_SHARE = 0.88;

// Where woods are conifer: boreal latitudes and high ground (tunable, not data).
const BOREAL_LAT = [48, 60] as const;
const MONTANE_ELEV_M = 1400;

export interface HexShapes {
  /** Ground level (sea floor, lowland, plateau). Shared points cluster on this: big steps are cliffs. */
  base: Float32Array;
  /** Hill and mountain uplift. Shared points average it smoothly, so ranges join into massifs. */
  lift: Float32Array;
  /** Extra height at the hex centre only (peaks and mound tops). */
  crown: Float32Array;
  /** Vertex noise amplitude. */
  amp: Float32Array;
  /** BIOME_COUNT weights per hex, 0..255. */
  weights: Uint8Array;
  /** AUX_COUNT values per hex, 0..255. */
  aux: Uint8Array;
  /** HEX_KIND per hex. */
  kind: Uint8Array;
  /** GROUND_COUNT weights per hex, 0..255 (land only; water hexes are all 0). */
  ground: Uint8Array;
  /** World y of the snow line from glacier data (NO_SNOW = none). */
  snowLine: Float32Array;
  /** Share of a non-mountain hex covered by glacier snow patches, 0..1. */
  snowPatch: Float32Array;
  /** Height of the hex's peak model (mountains only, else 0), world units. */
  peakH: Float32Array;
  /** Mountain crown scale (each peak its own height); 1 elsewhere. */
  crownScale: Float32Array;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

function isWater(terrain: number): boolean {
  return terrain === TERRAIN.OCEAN || terrain === TERRAIN.COAST;
}

/** Height of the raised plateau term for a mean elevation in metres. */
export function plateauHeight(elevM: number): number {
  if (elevM <= 0) {
    return 0;
  }
  return ELEV_GAIN * Math.pow(elevM / METRES_PER_KM, ELEV_POW);
}

/** Stable per-hex random value in [0, 1) for channel `k`. */
export function hexHash(t: number, k: number): number {
  let h = Math.imul(t + 1, 0x9e3779b1) ^ Math.imul(k + 7, 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** Which trees a woods hex holds: conifer on tundra, at boreal latitudes and on high ground. */
export function woodsKind(g: PhysicalGrid, i: number): WoodsKind {
  if (g.terrain[i] === TERRAIN.TUNDRA || g.elev[i] > MONTANE_ELEV_M) {
    return WOODS_KIND.CONIFER;
  }
  const boreal = smoothstep(BOREAL_LAT[0], BOREAL_LAT[1], Math.abs(g.lat[i]));
  if (boreal >= 0.85) {
    return WOODS_KIND.CONIFER;
  }
  return boreal > 0.2 ? WOODS_KIND.MIXED : WOODS_KIND.BROADLEAF;
}

/**
 * Height fraction of a mountain's peak above which its glacier cap starts (1 = no glacier). The cap
 * covers the share of the model's footprint that the hex's `ice` fraction says is ice.
 */
export function capFraction(g: PhysicalGrid, i: number): number {
  const ice = g.ice[i] / BYTE;
  if (ice < ICE_MIN) {
    return 1;
  }
  return 1 - Math.pow(Math.min(ice, ICE_CAP_MAX), 1 / (2 * PEAK_PROFILE));
}

/** A mountain hex's highest point (elev_max) as a 0..1 share of the peak model scale. */
export function peakShare(g: PhysicalGrid, i: number): number {
  return smoothstep(PEAK_ELEV_M[0], PEAK_ELEV_M[1], g.elev_max[i]);
}

/** 0..1: how much a mountain-class hex reads as a mountain (see FOOT_M, PLATEAU_M, PROMINENCE_M). */
function mountainness(g: PhysicalGrid, i: number): number {
  const foot = smoothstep(FOOT_M[0], FOOT_M[1], g.elev[i]);
  const plateau = smoothstep(PLATEAU_M[0], PLATEAU_M[1], g.elev[i]);
  const massif = smoothstep(PROMINENCE_M[0], PROMINENCE_M[1], g.elev_max[i] - g.elev[i]);
  return foot * (1 - plateau * (1 - massif));
}

/** True for a mountain-class hex that reads as rolling ground (foothill, plateau steppe): no peak model. */
export function rollingGround(g: PhysicalGrid, i: number): boolean {
  return g.hill[i] === HILL.MOUNTAINS && mountainness(g, i) < ROLLING_BELOW;
}

/** Whether mountain hex `j` outranks `i` for the horn: higher point, ties broken by a stable hash. */
function outranks(g: PhysicalGrid, j: number, i: number): boolean {
  if (g.elev_max[j] !== g.elev_max[i]) {
    return g.elev_max[j] > g.elev_max[i];
  }
  return hexHash(j, HERO_TIE_SALT) > hexHash(i, HERO_TIE_SALT);
}

/**
 * True when mountain hex `i` carries a hero horn: not a foothill (mean elevation), and the highest
 * point among the mountain hexes within HERO_RINGS. The others hold lumps, so a range reads as
 * ridges around a few massifs.
 */
export function peakHero(g: PhysicalGrid, i: number): boolean {
  if (g.elev[i] < HERO_MIN_MEAN_M || rollingGround(g, i)) {
    return false;
  }
  const seen = new Set<number>([i]);
  let edge = [i];
  for (let ring = 0; ring < HERO_RINGS; ring++) {
    const next: number[] = [];
    for (const h of edge) {
      for (let d = 0; d < 6; d++) {
        const n = neighbour(h, d);
        if (n < 0 || seen.has(n)) {
          continue;
        }
        seen.add(n);
        next.push(n);
        if (g.hill[n] === HILL.MOUNTAINS && !rollingGround(g, n) && outranks(g, n, i)) {
          return false;
        }
      }
    }
    edge = next;
  }
  return true;
}

/** Height of the tallest peak model on a mountain hex (a tunable scale, not data). */
function peakHeight(g: PhysicalGrid, i: number): number {
  const horn = PEAK_H[0] + (PEAK_H[1] - PEAK_H[0]) * peakShare(g, i);
  return peakHero(g, i) ? horn : horn * LUMP_HEIGHT;
}

/** Ground-cover weights of a land hex (GROUND channels, sum 1). */
function groundCover(g: PhysicalGrid, i: number, out: Float32Array): void {
  out.fill(0);
  const t = g.terrain[i];
  const baseChannel = [GROUND.PLAINS, GROUND.PLAINS, GROUND.PLAINS, GROUND.GRASS, GROUND.PLAINS, GROUND.DESERT, GROUND.TUNDRA, GROUND.SNOW][t] ?? GROUND.PLAINS;
  out[baseChannel] = 1;
  const f = g.feature[i];
  if (f === FEATURE.ICE) {
    out.fill(0);
    out[GROUND.SNOW] = 1;
    return;
  }
  if (f === FEATURE.RAINFOREST) {
    out[baseChannel] = 1 - RAINFOREST_SHARE;
    out[GROUND.JUNGLE] += RAINFOREST_SHARE;
    return;
  }
  if (f !== FEATURE.WOODS) {
    return;
  }
  const kind = woodsKind(g, i);
  const share = kind === WOODS_KIND.BROADLEAF ? WOODS_SHARE : CONIFER_SHARE;
  const conifer = kind === WOODS_KIND.CONIFER ? 1 : kind === WOODS_KIND.MIXED ? 0.5 : 0;
  out[baseChannel] = 1 - share;
  out[GROUND.CONIFER] += share * conifer;
  out[GROUND.BROADLEAF] += share * (1 - conifer);
}

/** Compute every hex's heights, weights and kind. */
export function shapeHexes(g: PhysicalGrid): HexShapes {
  const base = new Float32Array(HEX_COUNT);
  const lift = new Float32Array(HEX_COUNT);
  const crown = new Float32Array(HEX_COUNT);
  const amp = new Float32Array(HEX_COUNT);
  const weights = new Uint8Array(HEX_COUNT * BIOME_COUNT);
  const aux = new Uint8Array(HEX_COUNT * AUX_COUNT);
  const kind = new Uint8Array(HEX_COUNT);
  const ground = new Uint8Array(HEX_COUNT * GROUND_COUNT);
  const snowLine = new Float32Array(HEX_COUNT).fill(NO_SNOW);
  const snowPatch = new Float32Array(HEX_COUNT);
  const peakH = new Float32Array(HEX_COUNT);
  const crownScale = new Float32Array(HEX_COUNT).fill(1);
  const cover = new Float32Array(GROUND_COUNT);

  for (let i = 0; i < HEX_COUNT; i++) {
    const t = g.terrain[i];
    const w = new Float32Array(BIOME_COUNT);

    // Water: sea floor from bathymetry; the ocean plane draws the surface.
    if (isWater(t)) {
      const depth = Math.max(0, -g.elev[i]);
      kind[i] = HEX_KIND.WATER;
      base[i] = t === TERRAIN.COAST
        ? COAST_TOP - COAST_DROP * Math.min(1, depth / COAST_DEPTH_M)
        : OCEAN_TOP - OCEAN_DROP * Math.min(1, depth / OCEAN_DEPTH_M);
      w[BIOME.SEABED] = 1;
      writeWeights(weights, i, w);
      continue;
    }

    const plateau = plateauHeight(g.elev[i]);

    // Lakes: flat water drawn by the lake surface layer at the lake's own altitude.
    if (t === TERRAIN.LAKE) {
      kind[i] = HEX_KIND.LAKE;
      base[i] = LAND_BASE + plateau - LAKE_DIP;
      w[BIOME.SEABED] = 1;
      writeWeights(weights, i, w);
      continue;
    }

    kind[i] = HEX_KIND.LAND;
    const hill = g.hill[i];
    base[i] = LAND_BASE + plateau;
    amp[i] = AMP_FLAT;
    const reliefKm = Math.min(MAX_RELIEF_KM, g.relief[i] / METRES_PER_KM);
    amp[i] += AMP_PER_RELIEF_KM * Math.min(AMP_RELIEF_MAX_KM, reliefKm);
    if (hill === HILL.HILLS) {
      lift[i] = HILL_LIFT;
      crown[i] = HILL_CROWN;
      amp[i] = Math.max(amp[i], AMP_HILLS);
    } else if (hill === HILL.MOUNTAINS) {
      // Rolling ground (mountainness ~ 0) rises like hills; a real mountain takes the full uplift.
      const m = mountainness(g, i);
      lift[i] = HILL_LIFT + (MOUNTAIN_LIFT + RELIEF_LIFT * reliefKm - HILL_LIFT) * m;
      crown[i] = HILL_CROWN + (MOUNTAIN_CROWN + RELIEF_CROWN * reliefKm - HILL_CROWN) * m;
      amp[i] = Math.max(amp[i], AMP_HILLS + (AMP_MOUNTAINS - AMP_HILLS) * m);
      crownScale[i] = CROWN_SCALE[0] + (CROWN_SCALE[1] - CROWN_SCALE[0]) * hexHash(i, 0);
      peakH[i] = peakHeight(g, i);
    }

    // Base terrain channel, then features take a share of it.
    const terrainBiome = [BIOME.SEABED, BIOME.SEABED, BIOME.SEABED, BIOME.GRASS, BIOME.PLAINS, BIOME.DESERT, BIOME.TUNDRA, BIOME.SNOW][t] ?? BIOME.PLAINS;
    w[terrainBiome] = 1;
    const f = g.feature[i];
    if (f === FEATURE.WOODS) {
      w[terrainBiome] = 1 - WOODS_SHARE;
      w[BIOME.FOREST] += WOODS_SHARE;
    } else if (f === FEATURE.RAINFOREST) {
      w[terrainBiome] = 1 - RAINFOREST_SHARE;
      w[BIOME.JUNGLE] += RAINFOREST_SHARE;
    } else if (f === FEATURE.ICE) {
      w.fill(0);
      w[BIOME.SNOW] = 1;
    }
    writeWeights(weights, i, w);
    groundCover(g, i, cover);
    for (let c = 0; c < GROUND_COUNT; c++) {
      ground[i * GROUND_COUNT + c] = Math.round(cover[c] * BYTE);
    }

    const rock = rollingGround(g, i) ? HILL_ROCK : hill === HILL.MOUNTAINS ? 1 : hill === HILL.HILLS ? HILL_ROCK : 0;
    aux[i * AUX_COUNT + AUX.ROCK] = Math.round(rock * BYTE);
    aux[i * AUX_COUNT + AUX.MARSH] = f === FEATURE.MARSH ? BYTE : 0;
    aux[i * AUX_COUNT + AUX.FLOOD] = f === FEATURE.FLOODPLAINS ? BYTE : 0;

    // Snow from glacier data: a cap on a mountain's peak (glaciated mountains too: their ground
    // is the snow biome, but the rock layer of a range would paint it brown), patches elsewhere
    // (an ice sheet is snow ground already).
    const ice = g.ice[i] / BYTE;
    if (ice < ICE_MIN) {
      continue;
    }
    if (hill === HILL.MOUNTAINS) {
      const peakBase = base[i] + lift[i] + crown[i] * crownScale[i] * PEAK_BASE_CROWN;
      snowLine[i] = peakBase + peakH[i] * capFraction(g, i);
      continue;
    }
    if (f !== FEATURE.ICE) {
      snowPatch[i] = ice;
    }
  }

  // Lakes take their shores' ground in the vertex weights: a sea-floor weight there would ring
  // every lake with beach (the lake layer draws the water and a soft reed-and-sand shore).
  for (let i = 0; i < HEX_COUNT; i++) {
    if (kind[i] !== HEX_KIND.LAKE) {
      continue;
    }
    const sum = new Float32Array(BIOME_COUNT);
    let n = 0;
    for (let d = 0; d < 6; d++) {
      const nb = neighbour(i, d);
      if (nb < 0 || kind[nb] !== HEX_KIND.LAND) {
        continue;
      }
      for (let b = 0; b < BIOME_COUNT; b++) {
        sum[b] += weights[nb * BIOME_COUNT + b];
      }
      n++;
    }
    if (n === 0) {
      continue;
    }
    for (let b = 0; b < BIOME_COUNT; b++) {
      weights[i * BIOME_COUNT + b] = Math.round(sum[b] / n);
    }
  }

  // Open ocean far from any shelf is not meshed: the opaque deep water hides it.
  for (let i = 0; i < HEX_COUNT; i++) {
    if (g.terrain[i] !== TERRAIN.OCEAN) {
      continue;
    }
    let open = true;
    for (let d = 0; d < 6 && open; d++) {
      const n = neighbour(i, d);
      open = n < 0 || g.terrain[n] === TERRAIN.OCEAN;
    }
    if (open) {
      kind[i] = HEX_KIND.DEEP;
    }
  }

  return { base, lift, crown, amp, weights, aux, kind, ground, snowLine, snowPatch, peakH, crownScale };
}

function writeWeights(out: Uint8Array, hex: number, w: Float32Array): void {
  for (let b = 0; b < BIOME_COUNT; b++) {
    out[hex * BIOME_COUNT + b] = Math.round(w[b] * BYTE);
  }
}

// ─── CPU noise (mirrors the periodic lattice idea of tsl/noise.ts) ────────────────────────────────

function hash2(x: number, z: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(z | 0, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/** Value noise in [0, 1), periodic in x over `period` lattice cells. */
function vnoiseP(x: number, z: number, period: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = smooth(x - ix);
  const fz = smooth(z - iz);
  const x0 = ((ix % period) + period) % period;
  const x1 = (x0 + 1) % period;
  const a = hash2(x0, iz);
  const b = hash2(x1, iz);
  const c = hash2(x0, iz + 1);
  const d = hash2(x1, iz + 1);
  return (a + (b - a) * fx) * (1 - fz) + (c + (d - c) * fx) * fz;
}

const NOISE_OCTAVES: ReadonlyArray<readonly [freq: number, weight: number]> = [[0.9, 0.6], [2.3, 0.3], [5.3, 0.1]];
const NOISE_PERIODS = NOISE_OCTAVES.map(([f]) => Math.round(f * WORLD_W));

/** Signed ground undulation in [-1, 1] at world XZ; seamless across the east-west wrap. */
export function groundNoise(x: number, z: number): number {
  let n = 0;
  for (let o = 0; o < NOISE_OCTAVES.length; o++) {
    const period = NOISE_PERIODS[o];
    const s = period / WORLD_W;
    n += NOISE_OCTAVES[o][1] * vnoiseP(x * s, z * s + o * 7.3, period);
  }
  return n * 2 - 1;
}

/** Approximate ground height at a hex centre (for picking and markers). */
export function hexTopHeight(shapes: HexShapes, hex: number): number {
  if (hex < 0 || hex >= HEX_COUNT) {
    return SEA_Y;
  }
  if (shapes.kind[hex] === HEX_KIND.WATER || shapes.kind[hex] === HEX_KIND.DEEP) {
    return SEA_Y;
  }
  return shapes.base[hex] + shapes.lift[hex] + shapes.crown[hex];
}
